// helper which creates specific get logic
import startCase from 'lodash.startcase'
import camelCase from 'lodash.camelcase'
import to from 'await-to-js'
import isEqual from 'lodash.isequal'

import {
  addRequestAuthentication,
  addRequestFiltering,
  addRequestMasterKey,
  addRequestPagination,
  addRequestSorting,
  invokeHooks,
  pascalCase,
  promisifyRPC
} from '../utils'

export default ({ actionType, name, config, common }) => {
  let fn = async (context, params) => {
    const { commit, state, dispatch } = context
    // if we use master key, and if it's null/undefined, no other logic is needed
    if (config.items.masterKeyField && (state.masterActiveKey === undefined || state.masterActiveKey === null)) {
      // no need for errors, this is expected to happen
      dispatch('info', `${startCase(name)}: Skipping because master key is not defined`)
      return false
    }
    // 0) validate current state of pagination
    // TODO in some future revision
    // ----> for now: caller is responsible to clear items before making a new request or set pagination offset manually!
    // 1) prepare request object
    // - convention: uppercase first letter of name + add 'Request'
    let requestObjectType = `${pascalCase(name)}Request`
    let request = new config.service.import[requestObjectType]()
    // - add authentication
    addRequestAuthentication(config, context, request, common.actionDefinitions[actionType][name].typeMappings)
    // - check if it uses sort/pagination params and add them
    // - the sort/pagination is defined within the service state
    addRequestSorting(config, context, request, common.actionDefinitions[actionType][name].typeMappings)
    addRequestPagination(config, context, request, common.actionDefinitions[actionType][name].typeMappings)
    // - try to match other fields to allow filters: for example, request by uuid
    addRequestFiltering(config, context, request, params, common.actionDefinitions[actionType][name].typeMappings)
    // - add master key if present
    addRequestMasterKey(config, context, request, params, common.actionDefinitions[actionType][name].typeMappings)
    // - ALLOW HOOK to change something within the request or make a whole new request
    request = await invokeHooks(actionType, 'beforeDataRequest', name, { config, context, request, params })
    // if request is now null - some error happened!
    if (!request) {
      return false
    }
    // 2) call API
    // do some checks before making the request:
    // - in case the params are the same like in previous call, do nothing
    // - this can happen for example when we click sort column 3 times and get the same sort as before - no request should happen
    //      --> when updating the sort/filtering we need to check for this and prevent API call
    // - also prevent fetching if we are still retrieving previous request with same params
    let previousRequest = state.latestRequests[name] ? state.latestRequests[name] : { request: null, masterActiveKey: null, responseData: null }
    let sameAsPreviousRequest = isEqual(request, previousRequest.request)
    // check that we actually had a request before this one!
    if (previousRequest.request && sameAsPreviousRequest) {
      dispatch('warning', `${startCase(name)}: Attempted to trigger same request like previous one. Skipping`)
      return false
    }
    // save this request as the latest one (also sets error to null)
    commit('setLatestRequest', {
      name: name,
      masterActiveKey: state.masterActiveKey,
      request: request
    })
    // Invoke RPC and wait for it
    // - need to promisify GPRC call
    // - metadata is not used anywhere?
    let [err, data] = await to(promisifyRPC(config, name, request, context))
    // 3) GOT RESULTS
    // once we return here, we might not be processing the latest request anymore
    //  - we could have already switched MASTER ACTIVE ITEM as well and getting new items
    let isLastRequest = isEqual(request, state.latestRequests[name].request)
    if (!isLastRequest) {
      // this is expected to happen
      dispatch('info', `${startCase(name)}: Discarded result of older unfinished request.`)
      return false
    }

    // WE ARE PROCESSING LATEST REQUEST -->
    // log any kind of error, doesn't matter if this is the last request
    if (err) {
      // push the error info into state, maybe we will show it to user
      dispatch('error', {
        message: `${startCase(name)}: Error while retrieving data.`,
        error: err
      })
      commit('setLatestRequestError', { name: name, error: err })
      await invokeHooks(actionType, 'error', name, { config, context, request, params, data: err })
      commit('finishedLatestRequest', name)
      return false
    }
    // concurrent get requests for same item will not work, only the last one will be considered
    //        - if we fetch page 1, then immediately page 2, only page 2 is added
    //        - UI should prevent this from happening
    //        - in order for this to work we would need to save which pages are loaded and which are not etc... not needed anyway
    // master active key could be an object -> use isEqual for comparison
    let isMasterActiveKeySame = isEqual(state.masterActiveKey, state.latestRequests[name].masterActiveKey)
    if (!isMasterActiveKeySame) {
      // this is expected to happen, while retrieving first request, user changes to another folder
      dispatch('info', `${startCase(name)}: Discarding result because master is not same anymore`)
      commit('finishedLatestRequest', name)
      return false
    }
    // extract items from the data object - this is XXXResponse thing
    let responseData = data.toObject() // easier to handle JS objects than getXxxxList stuff

    // the pagination.afterKeyList is retrieved and sent back as is
    const newAfterKeyList = responseData.pagination ? responseData.pagination.afterKeyList : []
    commit('setPaginationAfterKeys', newAfterKeyList)

    // we expect to have xxxList within the response, the first one by convention is our main items list
    let firstListKey = Object.keys(responseData).find(k => k.endsWith('List'))
    let items = firstListKey ? responseData[firstListKey] : []
    // if we didn't retrieve any new items, we loaded everything
    // TODO: add possibility that response gives counts
    if (items.length === 0) {
      commit('setAllItemsLoaded', true)
    }

    // when GETing data, we sometimes get other data along with it
    // store the whole object in the store
    commit('setLatestResponse', { name, responseData })

    // we need to analyze the offset and see what we need to do with the results
    // - a new request / master key changed -> setItems
    // - go back a page -> prependItems
    // - go to next page -> appendItems
    if (!previousRequest.request || previousRequest.masterActiveKey !== state.masterActiveKey) {
      await dispatch('replaceItems', items)
    } else {
      // we have previous request and it has the same masterActiveKey => continue loading
      // if we have pagination, we check previous and current offset
      let prevRequestedOffset
      let requestedOffset
      // we can use pagination as object or as regular fields on request
      if (config.service.features.pagination) {
        let prevReq = previousRequest.request ? previousRequest.request.toObject() : null
        let req = request.toObject()
        let propName = camelCase(config.service.paginationObjectType)
        prevRequestedOffset = prevReq ? prevReq[propName].offset : null
        requestedOffset = req[propName].offset
      } else {
        // special case - we want pagination without a dedicated object
        if (config.service.paginationObjectType === null && request.getOffset) {
          prevRequestedOffset = previousRequest.request.getOffset()
          requestedOffset = request.getOffset()
        }
      }
      // if we didn't have any items before (new get with different params)
      // OR
      // if requested the same offset (this can happen when changing sort)
      // --> set the items
      if (state.items.length === 0 || requestedOffset === prevRequestedOffset) {
        await dispatch('replaceItems', items)
      } else {
        if (requestedOffset < prevRequestedOffset) {
          commit('prependItems', items)
        } else {
          commit('appendItems', items)
        }
      }
    }

    // call hook if needed
    await invokeHooks(actionType, 'afterDataRequest', name, { config, context, request, params, data: responseData })

    // all done
    commit('finishedLatestRequest', name)
    return true
  }
  return fn
}
